import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Switch, Drawer, message, Select, DatePicker, Button, Form, Tooltip } from 'antd';
import {
	ExportOutlined,
	RedoOutlined,
	PlusOutlined,
	EyeOutlined,
	FileTextOutlined,
	CloseOutlined,
	CloseCircleOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import styled from 'styled-components';
import { ButtonWrapper } from 'components';
import { FilterAtnButton } from 'utils/commonStyled';
import { AntdTable } from 'components/custom-table/Style';
import { Box, Flex, Text } from 'atoms';
import { FilterTag, ACL } from 'components/common';
import {
	searchVehiclesV2,
	searchVehicleTypesV2,
	updateVehicleV2,
	exportVehiclesV2,
} from 'store/vehicleSliceV2';
import { searchVendorV2 } from 'store/vendorSliceV2';
import { usePersistedState } from 'hooks';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const IST_OFFSET_IN_MS = 5.5 * 60 * 60 * 1000;

const LtSearchBar = styled(Search)`
	border: none;
`;

const WrapSelect = styled(Select)`
	.ant-select-selector {
		height: auto !important;
		min-height: 40px !important;
	}
	.ant-select-selection-item {
		white-space: normal !important;
		word-break: break-word !important;
		line-height: normal !important;
		padding-top: 4px;
		padding-bottom: 4px;
	}
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 16px;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

const ViewButton = styled(ButtonWrapper)`
	padding: 4px;
	min-width: 32px;
	min-height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const DocumentIcon = styled(FileTextOutlined)`
	font-size: 18px;
	color: #1890ff;
	cursor: pointer;
	transition: color 0.2s;

	&:hover {
		color: #40a9ff;
	}
`;

const PendingDocumentStatus = styled(Flex)`
	align-items: center;
	gap: 6px;
	color: #f59e0b;
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
`;

const FilterForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 20px;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #374151;
	}
`;

const FilterHeader = styled(Flex)`
	justify-content: space-between;
	align-items: center;
	padding: 16px 0;
`;

const FilterTitleText = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #333;
`;

const FilterCloseButton = styled(CloseCircleOutlined)`
	font-size: 20px;
	color: #999;
	cursor: pointer;

	&:hover {
		color: #666;
	}
`;

const FilterButtonContainer = styled(Flex)`
	margin-top: 24px;
	gap: 12px;
`;

const ResetButton = styled(Button)`
	width: 100%;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	border: 1px solid #d1d5db;
	color: #374151;
	background: white;

	&:hover {
		border-color: #9ca3af;
		color: #1f2937;
		background: white;
	}
`;

const mapVendorToOption = (vendor) => {
	const vendorId = vendor?.id ?? vendor?.vendor_id ?? vendor?.value;
	const vendorName = vendor?.vendor_name ?? vendor?.name ?? vendor?.label ?? `Vendor ${vendorId}`;

	return {
		value: vendorId,
		label: vendorName,
	};
};

const mergeVendorOptions = (...vendorLists) => {
	const optionsMap = new Map();

	vendorLists
		.flat()
		.filter(Boolean)
		.map(mapVendorToOption)
		.forEach((vendor) => {
			if (vendor.value !== null && vendor.value !== '') {
				optionsMap.set(vendor.value, vendor);
			}
		});

	return Array.from(optionsMap.values()).sort((firstVendor, secondVendor) =>
		firstVendor.label.localeCompare(secondVendor.label)
	);
};

const getTableColumns = (handleDocumentPreview, handleViewVehicle, handleStatusChange) => [
	{
		title: 'Vehicle Number',
		dataIndex: 'vehicle_number',
		key: 'vehicle_number',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Vendor Name',
		dataIndex: 'vendor_name',
		key: 'vendor_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Vehicle Type',
		dataIndex: 'vehicle_type_name',
		key: 'vehicle_type',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'RC Document',
		dataIndex: 'document_info',
		key: 'rc_document',
		render: (documentInfo, record) => {
			const key =
				documentInfo?.rc?.document_key ||
				record.document_info?.rc?.document_key ||
				record.rc_document_key;
			if (!key) {
				if (record.documents_complete === false) {
					return (
						<PendingDocumentStatus>
							<ExclamationCircleOutlined style={{ fontSize: 14 }} />
							<Text style={{ color: 'inherit', fontSize: 12, fontWeight: 600 }}>
								Pending
							</Text>
						</PendingDocumentStatus>
					);
				}
				return <Text>-</Text>;
			}
			return (
				<DocumentIcon
					onClick={() => handleDocumentPreview(key, 'RC Document')}
					title="Click to view RC Document"
				/>
			);
		},
	},
	{
		title: 'Insurance Document',
		dataIndex: 'document_info',
		key: 'insurance_document',
		render: (documentInfo, record) => {
			const key =
				documentInfo?.insurance?.document_key ||
				record.document_info?.insurance?.document_key ||
				record.insurance_document_key;
			if (!key) {
				if (record.documents_complete === false) {
					return (
						<PendingDocumentStatus>
							<ExclamationCircleOutlined style={{ fontSize: 14 }} />
							<Text style={{ color: 'inherit', fontSize: 12, fontWeight: 600 }}>
								Pending
							</Text>
						</PendingDocumentStatus>
					);
				}
				return <Text>-</Text>;
			}
			return (
				<DocumentIcon
					onClick={() => handleDocumentPreview(key, 'Insurance Document')}
					title="Click to view Insurance Document"
				/>
			);
		},
	},
	{
		title: 'Created at',
		dataIndex: 'created_at',
		key: 'created_at',
		render: (text) => {
			if (!text) return <Text>-</Text>;
			const date = new Date(new Date(text).getTime() + IST_OFFSET_IN_MS);
			const day = date.getDate().toString().padStart(2, '0');
			const monthNames = [
				'Jan',
				'Feb',
				'Mar',
				'Apr',
				'May',
				'Jun',
				'Jul',
				'Aug',
				'Sep',
				'Oct',
				'Nov',
				'Dec',
			];
			const month = monthNames[date.getMonth()];
			const year = date.getFullYear();
			const hours = date.getHours();
			const minutes = date.getMinutes().toString().padStart(2, '0');
			const ampm = hours >= 12 ? 'pm' : 'am';
			const displayHours = hours % 12 || 12;
			return (
				<Text>
					<span style={{ display: 'block', whiteSpace: 'nowrap' }}>
						{day} {month} {year}
					</span>
					<span style={{ display: 'block', whiteSpace: 'nowrap' }}>
						{displayHours}:{minutes} {ampm}
					</span>
				</Text>
			);
		},
	},
	{
		title: 'Created by',
		dataIndex: 'created_by_name',
		key: 'created_by_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Status',
		dataIndex: 'is_active',
		key: 'is_active',
		render: (status, record) => (
			<Flex alignItems="center" gap="8px">
				<Switch
					checked={status}
					checkedChildren="Active"
					unCheckedChildren="Inactive"
					onChange={(checked) => handleStatusChange(checked, record)}
				/>
			</Flex>
		),
	},
	{
		title: 'View',
		dataIndex: 'id',
		key: 'view',
		render: (id, record) => (
			<ViewButton onClick={() => handleViewVehicle(record)}>
				<EyeOutlined style={{ fontSize: 18, color: '#1890ff' }} />
			</ViewButton>
		),
	},
];

export default function VehicleListPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [filterForm] = Form.useForm();
	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [searchValue, setSearchValue] = usePersistedState('vehicleList:search', '');
	const [appliedFilters, setAppliedFilters] = usePersistedState('vehicleList:filters', {});
	const [vehicles, setVehicles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = usePersistedState('vehicleList:pagination', {
		current: 1,
		pageSize: 20,
		total: 0,
	});
	const [filePreviewVisible, setFilePreviewVisible] = useState(false);
	const [previewFileKey, setPreviewFileKey] = useState(null);
	const [previewFileName, setPreviewFileName] = useState('');
	const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
	const [vendorOptions, setVendorOptions] = useState([]);
	const filterChangeTimeoutRef = useRef(null);
	const vendorSearchTimeoutRef = useRef(null);

	const fetchVehicles = async (page = 1, filters = null, pageSize = pagination.pageSize) => {
		setLoading(true);
		try {
			const activeFilters = filters !== null ? filters : appliedFilters;

			const response = await dispatch(
				searchVehiclesV2({
					filter_by: activeFilters,
					pagination: {
						page,
						per_page: pageSize,
					},
					sort_by: {
						field: 'created_at',
						order: 'desc',
					},
				})
			);

			if (response) {
				setVehicles(response.rows || []);
				setPagination((prev) => ({
					...prev,
					current: page,
					pageSize,
					total: response.count || 0,
				}));
			}
		} catch (error) {
			console.error('Error fetching vehicles:', error);
			message.error('Failed to fetch vehicles');
		} finally {
			setLoading(false);
		}
	};

	const fetchVehicleTypes = async () => {
		try {
			const response = await dispatch(
				searchVehicleTypesV2({
					pagination: { page: 1, per_page: 1000 },
				})
			);

			if (response && response.rows && Array.isArray(response.rows)) {
				// Options for filter should use vehicle_type_id, matching vehicle records/search API
				const filterOptions = response.rows
					.filter((item) => item && item.id && item.vehicle_type_name)
					.map((item) => ({
						value: item.id,
						label: item.vehicle_type_name,
					}));
				setVehicleTypeOptions(filterOptions);
			} else {
				message.error('Failed to fetch vehicle types');
			}
		} catch (error) {
			console.error(' Error fetching vehicle types:', error);
			message.error('Failed to fetch vehicle types');
		}
	};

	const fetchVendorOptions = useCallback(
		async (searchTerm = '', selectedVendorId = null) => {
			try {
				const requests = [
					dispatch(
						searchVendorV2({
							filter_by: searchTerm ? { query_str: searchTerm } : {},
							pagination: { page: 1, per_page: 20 },
						})
					),
				];

				if (selectedVendorId) {
					requests.push(
						dispatch(
							searchVendorV2({
								filter_by: { id: Number(selectedVendorId) },
								pagination: { page: 1, per_page: 20 },
							})
						)
					);
				}

				const [searchResponse, selectedVendorResponse] = await Promise.all(requests);
				setVendorOptions(
					mergeVendorOptions(
						searchResponse?.rows || [],
						selectedVendorResponse?.rows || []
					)
				);
			} catch (error) {
				console.error('Error fetching vendors:', error);
				message.error('Failed to fetch vendors');
			}
		},
		[dispatch]
	);

	useEffect(() => {
		fetchVehicles(
			pagination.current,
			{ ...appliedFilters, ...(searchValue ? { vehicle_number: searchValue } : {}) },
			pagination.pageSize
		);
		// Cleanup timeout on unmount
		return () => {
			if (filterChangeTimeoutRef.current) {
				clearTimeout(filterChangeTimeoutRef.current);
			}
			if (vendorSearchTimeoutRef.current) {
				clearTimeout(vendorSearchTimeoutRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Helper function to build form values from applied filters
	const buildFormValues = (filters) => {
		const formValues = {};

		// Status filter - explicitly handle both true and false
		if (filters.is_active !== undefined && filters.is_active !== null) {
			formValues.status =
				filters.is_active === true || filters.is_active === 'true' ? 'true' : 'false';
		}
		if (filters.vehicle_number) {
			formValues.vehicle_number = filters.vehicle_number;
		}
		if (filters.vendor_id) {
			formValues.vendor = filters.vendor_id;
		}
		if (filters.vehicle_type_id) {
			formValues.vehicle_type = filters.vehicle_type_id;
		}
		if (filters.created_at_from && filters.created_at_to) {
			formValues.dateRange = [dayjs(filters.created_at_from), dayjs(filters.created_at_to)];
		}

		return formValues;
	};

	// Populate filter form when drawer opens (same pattern as VehicleTypeListPageV2)
	useEffect(() => {
		if (filterDrawerOpen) {
			fetchVendorOptions('', appliedFilters.vendor_id);

			// Ensure vehicle types are loaded if needed (async, but don't block)
			if (appliedFilters.vehicle_type_id && vehicleTypeOptions.length === 0) {
				fetchVehicleTypes().then(() => {
					// After vehicle types load, populate form
					setTimeout(() => {
						const formValues = buildFormValues(appliedFilters);
						filterForm.setFieldsValue(formValues);
					}, 100);
				});
			} else {
				// No vehicle types needed, populate form immediately
				setTimeout(() => {
					const formValues = buildFormValues(appliedFilters);
					filterForm.setFieldsValue(formValues);
				}, 150);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterDrawerOpen, appliedFilters]);

	const handleVendorSearch = useCallback(
		(value) => {
			if (vendorSearchTimeoutRef.current) {
				clearTimeout(vendorSearchTimeoutRef.current);
			}

			vendorSearchTimeoutRef.current = setTimeout(() => {
				fetchVendorOptions(value, filterForm.getFieldValue('vendor'));
			}, 300);
		},
		[fetchVendorOptions, filterForm]
	);

	const handleSearch = (value) => {
		const trimmedValue = value ? value.trim() : '';
		setSearchValue(trimmedValue);
		const filters = {
			...appliedFilters,
			...(trimmedValue ? { vehicle_number: trimmedValue } : {}),
		};
		fetchVehicles(1, filters);
	};

	const handleExport = async () => {
		try {
			message.loading({ content: 'Generating export file...', key: 'export' });

			// Call export API with current filters
			const response = await dispatch(
				exportVehiclesV2({
					filter_by: {
						...appliedFilters,
						...(searchValue ? { vehicle_number: searchValue } : {}),
					},
				})
			);

			if (response && response.status === 'success' && response.data) {
				const { download_url, file_name, total_records } = response.data;

				// Create a temporary link to download the file
				const link = document.createElement('a');
				link.href = download_url;
				link.setAttribute('download', file_name);
				link.setAttribute('target', '_blank');
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				message.success({
					content: `Successfully exported ${total_records} vehicle${
						total_records !== 1 ? 's' : ''
					}`,
					key: 'export',
					duration: 3,
				});
			} else {
				message.warning({
					content: response?.message || 'No data available for export',
					key: 'export',
					duration: 3,
				});
			}
		} catch (error) {
			console.error('Export error:', error);
			message.error({
				content: error?.message || 'Failed to export vehicles',
				key: 'export',
				duration: 3,
			});
		}
	};

	const handleSync = async () => {
		try {
			message.loading({ content: 'Syncing vehicles...', key: 'sync' });

			// Refetch vehicles with current filters
			await fetchVehicles(pagination.current, {
				...appliedFilters,
				...(searchValue ? { vehicle_number: searchValue } : {}),
			});

			message.success({ content: 'Vehicles synced successfully', key: 'sync', duration: 2 });
		} catch (error) {
			console.error('Sync error:', error);
			message.error({ content: 'Failed to sync vehicles', key: 'sync', duration: 3 });
		}
	};

	const handleCreate = () => {
		navigate('/vehicles/add');
	};

	const handleStatusChange = async (checked, record) => {
		try {
			const response = await dispatch(
				updateVehicleV2({
					req_data: {
						id: record.id,
						is_active: checked,
					},
				})
			);

			if (response && response.status === 'success') {
				// Update local state to reflect the change
				setVehicles((prevVehicles) =>
					prevVehicles.map((vehicle) =>
						vehicle.id === record.id ? { ...vehicle, is_active: checked } : vehicle
					)
				);
			}
		} catch (error) {
			console.error('Error updating vehicle status:', error);
			message.error('Failed to update vehicle status');
		}
	};

	const handleDocumentPreview = (fileKey, fileName = '') => {
		if (!fileKey) {
			message.warning('Document not available');
			return;
		}
		setPreviewFileKey(fileKey);
		setPreviewFileName(fileName);
		setFilePreviewVisible(true);
	};

	const handleFilePreviewClose = () => {
		setFilePreviewVisible(false);
		setPreviewFileKey(null);
		setPreviewFileName('');
	};

	// Helper function to build filters from form values
	const buildFiltersFromFormValues = (values) => {
		const filters = {};

		// Status filter
		if (values.status !== undefined && values.status !== null && values.status !== '') {
			filters.is_active = values.status === 'true' || values.status === true;
		}

		// Vehicle Number filter
		if (values.vehicle_number) {
			filters.vehicle_number = values.vehicle_number;
		}

		// Vehicle Type filter
		if (values.vehicle_type) {
			filters.vehicle_type_id = Number(values.vehicle_type);
		}

		// Vendor filter
		if (values.vendor) {
			filters.vendor_id = values.vendor;
		}
		// Date range filter
		if (values.dateRange && values.dateRange.length === 2) {
			const startDate = values.dateRange[0];
			const endDate = values.dateRange[1];
			if (startDate && endDate) {
				filters.created_at_from = startDate.startOf('day').toISOString();
				filters.created_at_to = endDate.endOf('day').toISOString();
			}
		}

		return filters;
	};

	// Auto-apply filters when form values change
	const handleFilterChange = (changedValues) => {
		// Clear any existing timeout
		if (filterChangeTimeoutRef.current) {
			clearTimeout(filterChangeTimeoutRef.current);
		}

		// If vehicle_number changed, debounce it (500ms delay)
		if (changedValues.vehicle_number !== undefined) {
			filterChangeTimeoutRef.current = setTimeout(() => {
				const currentValues = filterForm.getFieldsValue();
				const filters = buildFiltersFromFormValues(currentValues);
				setAppliedFilters(filters);
				fetchVehicles(1, {
					...(searchValue ? { vehicle_number: searchValue } : {}),
					...filters,
				});
			}, 500);
		} else {
			// For other fields (Select, Checkbox, DatePicker), apply immediately
			const allValues = filterForm.getFieldsValue();
			const filters = buildFiltersFromFormValues(allValues);
			setAppliedFilters(filters);
			fetchVehicles(1, {
				...(searchValue ? { vehicle_number: searchValue } : {}),
				...filters,
			});
		}
	};

	const handleResetFilters = () => {
		if (filterChangeTimeoutRef.current) {
			clearTimeout(filterChangeTimeoutRef.current);
		}
		if (vendorSearchTimeoutRef.current) {
			clearTimeout(vendorSearchTimeoutRef.current);
		}
		filterForm.resetFields();
		fetchVendorOptions('', null);
		setAppliedFilters({});
		fetchVehicles(1, searchValue ? { vehicle_number: searchValue } : {});
	};

	const handleRemoveFilter = (filterKey) => {
		const newFilters = { ...appliedFilters };

		if (filterKey === 'dateRange') {
			delete newFilters.created_at_from;
			delete newFilters.created_at_to;
			filterForm.setFieldsValue({ dateRange: null });
		} else {
			delete newFilters[filterKey];

			if (filterKey === 'is_active') {
				filterForm.setFieldsValue({ status: undefined });
			} else if (filterKey === 'vehicle_number') {
				filterForm.setFieldsValue({ vehicle_number: undefined });
			} else if (filterKey === 'vendor_id') {
				filterForm.setFieldsValue({ vendor: undefined });
				fetchVendorOptions('', null);
			} else if (filterKey === 'vehicle_type_id') {
				filterForm.setFieldsValue({ vehicle_type: undefined });
			}
		}

		setAppliedFilters(newFilters);
		fetchVehicles(1, {
			...(searchValue ? { vehicle_number: searchValue } : {}),
			...newFilters,
		});
	};

	// Clear all filters
	const handleClearAllFilters = () => handleResetFilters();

	const getAppliedFiltersDisplay = () =>
		[
			appliedFilters.is_active !== undefined ? { key: 'is_active', label: 'Status' } : null,
			appliedFilters.vehicle_number
				? { key: 'vehicle_number', label: 'Vehicle Number' }
				: null,
			appliedFilters.vendor_id ? { key: 'vendor_id', label: 'Vendor' } : null,
			appliedFilters.vehicle_type_id
				? { key: 'vehicle_type_id', label: 'Vehicle Type' }
				: null,
			appliedFilters.created_at_from && appliedFilters.created_at_to
				? { key: 'dateRange', label: 'Created Date' }
				: null,
		].filter(Boolean);

	const activeFiltersCount = getAppliedFiltersDisplay().length;

	const handleTableChange = (paginationConfig, filters, sorter) => {
		const sortBy = sorter?.field
			? {
					field: sorter.field,
					order: sorter.order === 'ascend' ? 'asc' : 'desc',
			  }
			: {
					field: 'created_at',
					order: 'desc',
			  };

		setLoading(true);
		try {
			dispatch(
				searchVehiclesV2({
					filter_by: {
						...appliedFilters,
						...(searchValue ? { vehicle_number: searchValue } : {}),
					},
					pagination: {
						page: paginationConfig.current,
						per_page: paginationConfig.pageSize,
					},
					sort_by: sortBy,
				})
			).then((response) => {
				if (response) {
					setVehicles(response.rows || []);
					setPagination({
						current: paginationConfig.current,
						pageSize: paginationConfig.pageSize,
						total: response.count || 0,
					});
				}
			});
		} catch (error) {
			console.error('Error fetching vehicles:', error);
			message.error('Failed to fetch vehicles');
		} finally {
			setLoading(false);
		}
	};

	const handleViewVehicle = (vehicle) => {
		// Navigate to vehicle details page
		navigate(`/vehicles/${vehicle.vehicle_number}`, { state: { vehicle } });
	};

	return (
		<Box>
			<Flex justifyContent="space-between" alignItems="center" mt={2} mb={2}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							placeholder="Search"
							enterButton="Search"
							allowClear
							onSearch={handleSearch}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</Box>
					<StyledFilterButton
						onClick={() => {
							// Load vehicle types if not already loaded
							if (vehicleTypeOptions.length === 0) {
								fetchVehicleTypes();
							}
							setFilterDrawerOpen(true);
						}}
					>
						FILTERS {activeFiltersCount > 0 && `(${activeFiltersCount})`}
					</StyledFilterButton>
					<FilterDisplayBox>
						{getAppliedFiltersDisplay().map((filter) => (
							<FilterTag
								key={filter.key}
								onClose={() => handleRemoveFilter(filter.key)}
							>
								{filter.label}
							</FilterTag>
						))}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleClearAllFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex gap="10px">
					<ButtonWrapper onClick={handleExport} icons={<ExportOutlined />}>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					<ACL action="create" module="vehicle">
						<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
							CREATE
						</ButtonWrapper>
					</ACL>
				</Flex>
			</Flex>

			<Box mt={3}>
				<AntdTable
					rowKey="id"
					columns={getTableColumns(
						handleDocumentPreview,
						handleViewVehicle,
						handleStatusChange
					)}
					dataSource={vehicles}
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} vehicles`,
					}}
					onChange={handleTableChange}
				/>
			</Box>

			<FilePreviewModal
				visible={filePreviewVisible}
				fileKey={previewFileKey}
				fileName={previewFileName}
				entity="vendor"
				onClose={handleFilePreviewClose}
			/>

			<Drawer
				className="userList-drawer"
				placement="right"
				closable={false}
				onClose={() => setFilterDrawerOpen(false)}
				open={filterDrawerOpen}
				title={null}
				headerStyle={{ display: 'none' }}
			>
				<Box>
					<FilterHeader>
						<FilterTitleText>Filters</FilterTitleText>
						<FilterCloseButton onClick={() => setFilterDrawerOpen(false)} />
					</FilterHeader>

					<FilterForm
						form={filterForm}
						layout="vertical"
						onValuesChange={handleFilterChange}
					>
						<Form.Item label="Status" name="status">
							<Select placeholder="Select status" allowClear size="large">
								<Option value="true">Active</Option>
								<Option value="false">Inactive</Option>
							</Select>
						</Form.Item>

						<Form.Item label="Vehicle Number" name="vehicle_number">
							<Input
								placeholder="Enter vehicle number"
								allowClear
								size="large"
								onPressEnter={() => {
									// Clear debounce and apply immediately on Enter
									if (filterChangeTimeoutRef.current) {
										clearTimeout(filterChangeTimeoutRef.current);
									}
									const values = filterForm.getFieldsValue();
									const filters = buildFiltersFromFormValues(values);
									setAppliedFilters(filters);
									fetchVehicles(1, {
										...(searchValue ? { vehicle_number: searchValue } : {}),
										...filters,
									});
								}}
							/>
						</Form.Item>

						<Form.Item label="Vendor" name="vendor">
							<WrapSelect
								placeholder="Select vendor"
								allowClear
								size="large"
								showSearch
								filterOption={false}
								onSearch={handleVendorSearch}
								onDropdownVisibleChange={(open) => {
									if (open) {
										fetchVendorOptions('', filterForm.getFieldValue('vendor'));
									}
								}}
								onClear={() => {
									fetchVendorOptions('', null);
								}}
							>
								{vendorOptions.map((vendor) => (
									<Option key={vendor.value} value={vendor.value}>
										{vendor.label}
									</Option>
								))}
							</WrapSelect>
						</Form.Item>

						<Form.Item label="Vehicle Type" name="vehicle_type">
							<WrapSelect
								placeholder="Select vehicle type"
								allowClear
								size="large"
								showSearch
								filterOption={(input, option) =>
									(option?.label ?? '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								{vehicleTypeOptions
									.filter((option) => option && option.value && option.label)
									.map((option) => (
										<Option
											key={`vehicle-type-${option.value}`}
											value={option.value}
											label={option.label}
										>
											{option.label}
										</Option>
									))}
							</WrapSelect>
						</Form.Item>
						<Form.Item label="Created Date" name="dateRange">
							<RangePicker
								style={{ width: '100%' }}
								size="large"
								format="DD-MM-YYYY"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<FilterButtonContainer>
							<ResetButton onClick={handleResetFilters}>Reset</ResetButton>
						</FilterButtonContainer>
					</FilterForm>
				</Box>
			</Drawer>
		</Box>
	);
}
